import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { FaCheckCircle, FaPaintBrush, FaHammer, FaTools, FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Cta from '../../components/NewCallToAction';

const BRAND_BLUE = "#3b82f6";
const DARK_SLATE = "#0f172a";

const ServiceDetailPage = ({ title, subtitle, heroBg, description, highlights, coreCompetencies, faqs }) => {
  return (
    <main style={{ background: '#f8fafc', minHeight: '100vh', overflowX: 'hidden' }}>
      <Helmet>
        <title>{title} | Abraj Al Dhafna Qatar</title>
        <meta name="description" content={description.substring(0, 155)} />
        <link rel="canonical" href={`https://abrajaldhafna.com/services/${title.toLowerCase().replace(/ /g, '-')}`} />
      </Helmet>

      {/* Breadcrumb & Back */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '15px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', display: 'flex', gap: '10px', alignItems: 'center', fontSize: '14px' }}>
          <Link to="/" style={{ color: '#64748b', textDecoration: 'none' }}>Home</Link>
          <span style={{ color: '#cbd5e1' }}>/</span>
          <Link to="/services" style={{ color: '#64748b', textDecoration: 'none' }}>Services</Link>
          <span style={{ color: '#cbd5e1' }}>/</span>
          <span style={{ color: DARK_SLATE, fontWeight: 600 }}>{title}</span>
        </div>
      </div>

      {/* Header / Hero Section */}
      <section style={{ background: heroBg, padding: '100px 20px 80px', color: '#fff', position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span style={{ background: 'rgba(255,255,255,0.2)', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', marginBottom: '20px', display: 'inline-block' }}>CORE CAPABILITY</span>
            <h1 style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', fontWeight: 900, marginBottom: '20px', lineHeight: 1.1 }}>{title}</h1>
            <p style={{ fontSize: 'clamp(1.1rem, 2vw, 1.3rem)', opacity: 0.9, maxWidth: '700px', lineHeight: 1.6 }}>{subtitle}</p>
          </motion.div>
        </div>
        <div style={{ position: 'absolute', bottom: '-50px', right: '-50px', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)', borderRadius: '50%' }} />
      </section>

      {/* Main Narrative Section */}
      <section style={{ padding: '80px 20px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '60px' }}>
          <div>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, color: DARK_SLATE, marginBottom: '25px' }}>Excellence in {title} Execution</h2>
            <div style={{ fontSize: '16px', lineHeight: 1.8, color: '#475569', marginBottom: '30px' }}>
              <p style={{ marginBottom: '20px' }}>{description}</p>
              <p>Our interior specialists combine artistic vision with absolute structural precision. By utilizing premium surface materials, advanced moisture-testing diagnostics, and dust-contained sanding equipment, we preserve the finish quality while accelerating handover schedules.</p>
            </div>

            <h3 style={{ fontSize: '1.4rem', fontWeight: 700, color: DARK_SLATE, marginBottom: '20px' }}>Key Service Highlights</h3>
            <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              {highlights.map((highlight, i) => (
                <li key={i} style={{ display: 'flex', gap: '12px', alignItems: 'center', fontSize: '15px', color: '#475569' }}>
                  <FaCheckCircle style={{ color: "#8b5cf6", flexShrink: 0 }} />
                  <span>{highlight}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Side Stats / Summary */}
          <div style={{ background: '#fff', borderRadius: '24px', padding: '40px', boxShadow: '0 10px 30px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0', height: 'fit-content' }}>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: DARK_SLATE, marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f1f5f9' }}>Project Capabilities</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <span style={{ fontSize: '12px', textTransform: 'uppercase', fontWeight: 800, color: '#94a3b8', display: 'block', marginBottom: '5px' }}>Aesthetic Standard</span>
                <strong style={{ fontSize: '18px', color: DARK_SLATE }}>Turnkey Premium Finishing</strong>
              </div>
              <div>
                <span style={{ fontSize: '12px', textTransform: 'uppercase', fontWeight: 800, color: '#94a3b8', display: 'block', marginBottom: '5px' }}>Specializations</span>
                <strong style={{ fontSize: '18px', color: DARK_SLATE }}>Epoxy Floors, False Ceilings, Gypsum</strong>
              </div>
              <div>
                <span style={{ fontSize: '12px', textTransform: 'uppercase', fontWeight: 800, color: '#94a3b8', display: 'block', marginBottom: '5px' }}>Target Sectors</span>
                <strong style={{ fontSize: '18px', color: DARK_SLATE }}>Corporate Offices, Luxury Villas, Retail</strong>
              </div>
            </div>
            <Link to="/contact" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '40px', background: "#8b5cf6", color: '#fff', textDecoration: 'none', padding: '16px', borderRadius: '12px', fontWeight: 700, transition: 'transform 0.2s' }}>
              Request A Quote <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* In-Depth Competencies */}
      <section style={{ background: '#fff', padding: '80px 20px', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 900, color: DARK_SLATE, textAlign: 'center', marginBottom: '50px' }}>Detailed Competency Portfolio Portfolio</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
            {coreCompetencies.map((comp, i) => (
              <div key={i} style={{ background: '#f8fafc', padding: '30px', borderRadius: '20px', border: '1px solid #f1f5f9' }}>
                <div style={{ width: '50px', height: '50px', background: `#8b5cf615`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: "#8b5cf6", fontSize: '1.5rem', marginBottom: '20px' }}>{comp.icon}</div>
                <h4 style={{ fontSize: '1.2rem', fontWeight: 800, color: DARK_SLATE, marginBottom: '10px' }}>{comp.title}</h4>
                <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6 }}>{comp.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Cta />
    </main>
  );
};

const FitOutWorks = () => {
  const pageData = {
    title: "Interior Fit-Out & Finishing",
    subtitle: "Premium gypsum partitions, decorative false ceilings, industrial epoxy flooring, and custom commercial finishes.",
    heroBg: "linear-gradient(135deg, #4c1d95 0%, #8b5cf6 100%)",
    description: "Abraj Al Dhafna transforms raw structural hulls into stunning, operational environments across Qatar. Our interior fit-out squads manage precise drywall installation, false ceiling lattices (60x60 & custom gypsum), decorative glasswork, and high-end joinery. Additionally, we operate a specialized epoxy flooring and concrete polishing division to deliver flawless industrial and commercial floor layouts with long-term lifespan guarantees.",
    highlights: [
      "Gypsum Partitioning & Advanced Drywalling",
      "Custom Decorative & Standard 60x60 Ceilings",
      "Industrial Epoxy Coatings & Concrete Repair",
      "Waterproofing and Base Sealant Handover",
      "Premium Residential & Commercial Painting",
      "Flawless Tile, Marble, & Granite Laydowns"
    ],
    coreCompetencies: [
      {
        icon: <FaPaintBrush />,
        title: "Decorative & High-Build Painting",
        description: "Delivering residential textures, commercial eggshells, and heavy-grade industrial weatherproofing applications with zero brush-stroke defects."
      },
      {
        icon: <FaHammer />,
        title: "Gypsum & False Ceilings",
        description: "Fast deployment of false-ceiling framework, light-cove profiles, fire-rated drywall partition walls, and customized board alignments."
      },
      {
        icon: <FaTools />,
        title: "Epoxy Floor Subcontracting",
        description: "Complete concrete profiling, grinding, primer base application, and high-build chemical-resistant epoxy coats for warehouses, parking, and kitchens."
      }
    ]
  };

  return <ServiceDetailPage {...pageData} />;
};

export default FitOutWorks;
